// aiPopupResults — two-way awareness channel: popup results → chat.
//
// Popups (image, sound, etc.) call recordPopupResult when they successfully
// add an asset to the scene. The result is stored in window.__aideaRecentActions
// and broadcast as a CustomEvent so ChatPanel's RecentActivityStrip can show
// the user what the assistant's popups created.
//
// TODO(two-way v2): recent actions could later be forwarded to the agent on each
// request (e.g. via a custom header or initial message). Skipped for v1 because
// the agent is recreated on [widgetProps] changes, making per-request headers
// awkward, and the backend already has awareness via KB tools (listCourseImages /
// getCourseNarrations) that read the same idealStudioFiles rows these popups write.

import { useEffect, useState } from "react";

export const AI_RESULT_EVENT = "aidea:aiPopupResult";
const CLEARED_EVENT = "aidea:recentActionsCleared";
const MAX_ACTIONS = 12;

// Build a result entry and dispatch it. Entry shape: { feature, summary, url?, at }.
// Guards window so it's safe to call from any import context.
export function recordPopupResult(entry) {
  if (typeof window === "undefined") return;
  if (!entry || typeof entry.feature !== "string") return;

  const full = {
    feature: entry.feature,
    summary: typeof entry.summary === "string" ? entry.summary : entry.feature,
    at: Date.now(),
  };
  if (entry.url && typeof entry.url === "string") {
    full.url = entry.url;
  }

  if (!Array.isArray(window.__aideaRecentActions)) {
    window.__aideaRecentActions = [];
  }
  window.__aideaRecentActions.unshift(full);
  if (window.__aideaRecentActions.length > MAX_ACTIONS) {
    window.__aideaRecentActions.length = MAX_ACTIONS;
  }

  window.dispatchEvent(new CustomEvent(AI_RESULT_EVENT, { detail: full }));
}

export function getRecentActions() {
  if (typeof window === "undefined") return [];
  return window.__aideaRecentActions || [];
}

// React hook: returns { actions, dismiss(at), clear() }.
// actions = recent entries (newest first, max 12).
// dismiss removes one by `at` from state and from window.__aideaRecentActions.
// clear removes all.
export function useRecentActions() {
  const [actions, setActions] = useState(() => getRecentActions().slice());

  useEffect(() => {
    const onResult = (e) => {
      if (!e || !e.detail) return;
      setActions((prev) => {
        const next = [e.detail, ...prev];
        if (next.length > MAX_ACTIONS) next.length = MAX_ACTIONS;
        return next;
      });
    };

    const onCleared = () => {
      setActions([]);
    };

    window.addEventListener(AI_RESULT_EVENT, onResult);
    window.addEventListener(CLEARED_EVENT, onCleared);
    return () => {
      window.removeEventListener(AI_RESULT_EVENT, onResult);
      window.removeEventListener(CLEARED_EVENT, onCleared);
    };
  }, []);

  const dismiss = (at) => {
    setActions((prev) => prev.filter((a) => a.at !== at));
    if (Array.isArray(window.__aideaRecentActions)) {
      const idx = window.__aideaRecentActions.findIndex((a) => a.at === at);
      if (idx !== -1) window.__aideaRecentActions.splice(idx, 1);
    }
  };

  const clear = () => {
    if (typeof window !== "undefined") {
      window.__aideaRecentActions = [];
      window.dispatchEvent(new CustomEvent(CLEARED_EVENT));
    }
    setActions([]);
  };

  return { actions, dismiss, clear };
}
